import { useRef } from 'react';
import { useForm } from '@inertiajs/react';
import { Field } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { trackUmamiEvent } from '@/lib/analytics';

type ContactFormValues = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

type ContactFormProps = {
  className?: string;
};

export const ContactForm = ({ className }: ContactFormProps) => {
  const { data, setData, post, processing, errors } = useForm<ContactFormValues>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const hasStarted = useRef(false);

  const markStarted = () => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    trackUmamiEvent('contact_form_start');
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackUmamiEvent('contact_form_submit');
    post('/contact', {
      preserveScroll: true,
      onSuccess: () => {
        trackUmamiEvent('contact_form_success');
        setData({ name: '', email: '', subject: '', message: '' });
      },
      onError: (formErrors) => {
        trackUmamiEvent('contact_form_error', { fields: Object.keys(formErrors).join(',') });
      },
    });
  };

  return (
    <form className={className} onSubmit={onSubmit}>
      <Field
        label="Your full name"
        name="name"
        placeholder="Fill in your full name"
        value={data.name}
        onChange={(e) => {
          markStarted();
          setData('name', e.target.value);
        }}
        error={errors.name?.[0]}
      />
      <Field
        label="Your email"
        name="email"
        type="email"
        placeholder="you@email.com"
        value={data.email}
        onChange={(e) => {
          markStarted();
          setData('email', e.target.value);
        }}
        error={errors.email?.[0]}
      />
      <Field
        label="Subject"
        name="subject"
        placeholder="What's this about?"
        value={data.subject}
        onChange={(e) => {
          markStarted();
          setData('subject', e.target.value);
        }}
        error={errors.subject?.[0]}
      />
      <Field
        label="How can we help?"
        name="message"
        as="textarea"
        rows={5}
        placeholder="Tell us about your space, rooms, and how often you'd like a visit."
        value={data.message}
        onChange={(e) => {
          markStarted();
          setData('message', e.target.value);
        }}
        error={errors.message?.[0]}
      />
      <Button variant="solid" block type="submit" loading={processing} className="mt-2">
        Send message
      </Button>
    </form>
  );
};
